/**
 * RESPONSIBILITY: what a repeating date is.
 *
 * It replaced a cron expression. Cron was the right engine and the wrong language: an
 * administrator was asked to type `0 19 * * 2` for "Tuesdays at seven", and the person
 * who needed a weekly prayer call entered a single date instead, so the card kept saying
 * the day it was added. A picker writes the rule; nobody types it.
 *
 * STORED AS TEXT, in the shape of an iCalendar RRULE, restricted to what the picker offers:
 *
 *   FREQ=WEEKLY;BYDAY=TU,TH;BYHOUR=19;BYMINUTE=30     Tuesdays and Thursdays, 19:30
 *   FREQ=MONTHLY;BYDAY=3FR;BYHOUR=19;BYMINUTE=0       the third Friday, 19:00
 *   FREQ=MONTHLY;BYDAY=-1SU                           the last Sunday, no time of day
 *   FREQ=MONTHLY;BYMONTHDAY=15                        the 15th
 *   FREQ=YEARLY;BYMONTH=12;BYMONTHDAY=25              25 December
 *
 * Text rather than an object, so it is one value in every generator that already handles
 * strings — blanked, tidied, validated — and readable in the database. RRULE rather than a
 * format of our own, so it means the same thing to any calendar it is ever handed to.
 *
 * HALF A RULE IS LEGAL. "Weekly" with no day chosen yet saves, and simply has no next
 * date — content arrives over time. Only text that is not a rule at all is refused.
 *
 * Reading: repeat-parse.ts. Writing: repeat-format.ts. When it falls: repeat-next.ts.
 */

export type RepeatFreq = 'weekly' | 'monthly' | 'yearly';

/** Which of a month's weekdays: 1–4, or -1 for the last. */
export type Ordinal = 1 | 2 | 3 | 4 | -1;

export interface RepeatRule {
  freq: RepeatFreq;
  /** Weekly: the days, 0 = Sunday. Monthly by weekday: exactly one. */
  days: number[];
  /** Monthly by weekday: which one of them. Absent means monthly by date. */
  nth?: Ordinal;
  /** Monthly by date, and yearly: 1–31. */
  monthDay?: number;
  /** Yearly: 1–12. */
  month?: number;
  /** Time of day. Both or neither — a rule without one is a date, not midnight. */
  hour?: number;
  minute?: number;
}

/** RRULE's two-letter days, indexed the way Date.getDay() counts. */
export const DAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;

export const ORDINALS: readonly Ordinal[] = [1, 2, 3, 4, -1];

export const hasTime = (r: RepeatRule): boolean => r.hour !== undefined && r.minute !== undefined;
